import { useQuery } from '@apollo/client';
import i18n from '@risksmart-app/i18n/src/i18n';
import type { ActionRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  Action_Bool_Exp,
  GetActionsQuery,
  GetIssuesQuery,
  Issue_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetActionsDocument,
  GetIssuesDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import { mapTrpcActionsToGraphQL } from 'src/hooks/queries/action/useGetActionsRegister';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';
import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import type { TableFields, TableRecord } from '@/utils/table/types';

import { UNRATED } from '../../gigawidget/types';
import { createDataSource } from '../createDataSource';
import { defaultClickthroughFilter } from '../dataSourceHelpers';
import { simpleField, useRelationshipTableProps } from './relationship-helpers';

export type IssueActionRow = TableRecord & {
  Id: string;
  IssueId: string;
  IssueTitle: string;
  IssueStatus: string;
  ActionId: string;
  ActionTitle: string;
  ActionStatus: string;
  ActionPriority: string;
};

export default createDataSource({
  group: 'relationship',
  hasAccess: () => true,
  parentTypes: [Parent_Type_Enum.Issue, Parent_Type_Enum.Action],
  documentNode: GetActionsDocument,
  trpcQuery: (trpc) => {
    const queryOptions = trpc.frontend.action.register.queryOptions({});
    const transformedQueryOptions = {
      ...queryOptions,
      select: (data: ActionRegisterResponse) => mapTrpcActionsToGraphQL(data),
    };

    return transformedQueryOptions as UseQueryOptions<unknown, Error>;
  },
  useDefaultVariables: () => ({
    where: useEntityWhereFilter<Action_Bool_Exp>(Parent_Type_Enum.Action),
  }),
  useTablePropsHook: (
    data: GetActionsQuery | undefined,
    options: StatefulTableOptions<IssueActionRow>
  ) => {
    const fields = useMemo<TableFields<IssueActionRow>>(
      () => ({
        IssueTitle: simpleField(i18n.t('issues.columns.title')),
        IssueStatus: simpleField(i18n.t('issues.columns.status')),
        ActionTitle: simpleField(i18n.t('actions.columns.title')),
        ActionStatus: simpleField(i18n.t('actions.columns.status')),
        ActionPriority: simpleField(i18n.t('actions.columns.priority')),
      }),
      []
    );

    const issueWhere = useEntityWhereFilter<Issue_Bool_Exp>(
      Parent_Type_Enum.Issue
    );
    const { data: issueData } = useQuery(GetIssuesDocument, {
      variables: { where: issueWhere },
    });

    const joinedData = useMemo(() => {
      if (!data?.action || !issueData?.issue) {
        return undefined;
      }

      const issueMap = new Map(
        (issueData.issue as GetIssuesQuery['issue']).map((i) => [i.Id, i])
      );

      return data.action.flatMap((action) =>
        action.parents
          .filter(
            (p) =>
              p.parent?.ObjectType === Parent_Type_Enum.Issue && p.parent.Id
          )
          .map((parent) => {
            const issue = issueMap.get(parent.parent!.Id);

            return {
              Id: `${parent.parent!.Id}_${action.Id}`,
              IssueId: parent.parent!.Id,
              IssueTitle: issue?.Title ?? parent.issue?.Title ?? '',
              IssueStatus: '',
              ActionId: action.Id,
              ActionTitle: action.Title ?? '',
              ActionStatus: action.Status ?? '',
              ActionPriority: String(action.Priority ?? ''),
            };
          })
      );
    }, [data?.action, issueData?.issue]);

    return useRelationshipTableProps(
      joinedData,
      fields,
      i18n.t('issue_action_other'),
      options
    );
  },
  entityNamePlural: 'issue_action_other',
  entityNameSingular: 'issue_action_one',
  fields: 'issueActions.fields',
  dashboardFilterConfig: {},
  categoryGetters: [
    {
      id: 'actionStatus',
      name: () => i18n.t('actions.columns.status'),
      categoryGetter: (item: IssueActionRow) => item.ActionStatus || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionStatus'),
      ratingColourKey: 'action_status',
    },
    {
      id: 'actionPriority',
      name: () => i18n.t('actions.columns.priority'),
      categoryGetter: (item: IssueActionRow) => item.ActionPriority || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionPriority'),
      ratingColourKey: 'priority',
    },
    {
      id: 'issueTitle',
      name: () => i18n.t('issues.columns.title'),
      categoryGetter: (item: IssueActionRow) => item.IssueTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('IssueTitle'),
    },
    {
      id: 'actionTitle',
      name: () => i18n.t('actions.columns.title'),
      categoryGetter: (item: IssueActionRow) => item.ActionTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionTitle'),
    },
  ],
});
